import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from 'src/app/services';
import { PayrollAccountingDraft } from 'src/app/models/payroll-accounting-draft.model';
import { PayrollAccountingImport } from 'src/app/models/payroll-accounting-import.model';
import { HoldedChartOfAccount } from 'src/app/models/holded_chart_of_account.model';
import { Payroll } from 'src/app/models/payroll.model';
import { PAYROLL_CONCEPT_SLUG, payrollLineAmountBySlug } from 'src/app/models/payroll-line.utils';

export interface PayrollGestoriaImportReviewDialogData {
  import: PayrollAccountingImport;
  payrolls: Payroll[];
}

/** Bisual amounts used to verify PDF especie ↔ retribución flexible. */
export interface BisualPayrollCompare {
  flexible_remuneration: number;
}

@Component({
  selector: 'app-payroll-gestoria-import-review-dialog',
  templateUrl: './payroll-gestoria-import-review-dialog.component.html',
  styleUrls: ['./payroll-gestoria-import-review-dialog.component.css'],
  imports: [
    FormsModule,
    DecimalPipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatIconButton,
    MatFormField,
    MatSelect,
    MatOption,
    MatIcon,
    MatTooltip,
  ],
})
export class PayrollGestoriaImportReviewDialogComponent implements OnInit {
  private readonly dialog_ref = inject(MatDialogRef<PayrollGestoriaImportReviewDialogComponent>);
  private readonly api = inject(ApiService);
  private readonly snack = inject(MatSnackBar);
  readonly data = inject<PayrollGestoriaImportReviewDialogData>(MAT_DIALOG_DATA);

  drafts: PayrollAccountingDraft[] = [];
  salary_accounts: HoldedChartOfAccount[] = [];
  confirming = false;
  syncing_draft_id: number | null = null;

  ngOnInit(): void {
    this.drafts = [...(this.data.import.drafts ?? [])];
    this.api.listHoldedChartOfAccounts('payroll').subscribe({
      next: accounts => {
        this.salary_accounts = (accounts ?? []).filter(a => String(a.num ?? a.number ?? '').startsWith('640'));
      },
    });
  }

  payrollLabel(payroll: Payroll): string {
    const user = payroll.user;
    return user ? `${user.name} ${user.surnames}` : `Nómina #${payroll.id}`;
  }

  canSyncDraft(draft: PayrollAccountingDraft): boolean {
    return (
      draft.status !== 'synced' &&
      draft.payroll_id !== null &&
      !!draft.holded_salary_account_id &&
      this.syncing_draft_id === null &&
      !this.confirming
    );
  }

  /** Snapshot of Bisual Ret.flex for the matched employee (especie PDF). */
  bisualCompare(draft: PayrollAccountingDraft): BisualPayrollCompare | null {
    if (draft.payroll_id === null) {
      return null;
    }

    const payroll = this.data.payrolls.find(p => p.id === draft.payroll_id) as (Payroll & { flexible_remuneration?: number }) | undefined;

    if (!payroll) {
      return null;
    }

    const flexible = payrollLineAmountBySlug(
      payroll.payroll_lines ?? [],
      PAYROLL_CONCEPT_SLUG.FLEXIBLE_REMUNERATION,
      Number(payroll.flexible_remuneration ?? 0)
    );

    return {
      flexible_remuneration: Math.round(flexible * 100) / 100,
    };
  }

  amountsMatch(a: number | null | undefined, b: number | null | undefined, tolerance = 0.05): boolean {
    return Math.abs(Number(a ?? 0) - Number(b ?? 0)) <= tolerance;
  }

  private draftPayload(draft: PayrollAccountingDraft) {
    return {
      payroll_id: draft.payroll_id,
      salary: draft.salary,
      objectives_bonus: draft.objectives_bonus,
      employer_ss: draft.employer_ss,
      total_ss: draft.total_ss,
      irpf: draft.irpf,
      in_kind: draft.in_kind,
      total_to_pay: draft.total_to_pay,
      holded_salary_account_id: draft.holded_salary_account_id,
    };
  }

  private replaceDraft(updated: PayrollAccountingDraft): void {
    const idx = this.drafts.findIndex(d => d.id === updated.id);
    if (idx >= 0) {
      this.drafts[idx] = updated;
    }
  }

  saveDraft(draft: PayrollAccountingDraft): void {
    this.api.updatePayrollGestoriaDraft(draft.id, this.draftPayload(draft)).subscribe({
      next: updated => {
        this.replaceDraft(updated);
        this.snack.open('Borrador guardado', 'Ok', { duration: 2000 });
      },
      error: () => this.snack.open('No se pudo guardar el borrador', 'Ok', { duration: 3000 }),
    });
  }

  syncDraft(draft: PayrollAccountingDraft): void {
    if (!this.canSyncDraft(draft)) {
      if (!draft.payroll_id) {
        this.snack.open('Asigna un empleado Bisual antes de sincronizar', 'Ok', { duration: 3000 });
      } else if (!draft.holded_salary_account_id) {
        this.snack.open('Falta la cuenta Holded 640', 'Ok', { duration: 3000 });
      }
      return;
    }

    if (
      !confirm(
        `Se adjuntará el PDF a la nómina de Bisual, se actualizará Ret.flex y se sincronizará solo «${draft.extracted_name}» con Holded. ¿Continuar?`
      )
    ) {
      return;
    }

    this.syncing_draft_id = draft.id;
    this.api.updatePayrollGestoriaDraft(draft.id, this.draftPayload(draft)).subscribe({
      next: saved => {
        this.replaceDraft(saved);
        this.confirmDrafts([draft.id], {
          onDone: () => {
            this.syncing_draft_id = null;
          },
          onError: () => {
            this.syncing_draft_id = null;
          },
        });
      },
      error: () => {
        this.syncing_draft_id = null;
        this.snack.open('No se pudo guardar el borrador antes de sincronizar', 'Ok', { duration: 3000 });
      },
    });
  }

  confirm(): void {
    const pendingIds = this.drafts.filter(d => d.status !== 'synced').map(d => d.id);
    if (!pendingIds.length) {
      this.snack.open('No hay nóminas pendientes de sincronizar', 'Ok', { duration: 3000 });
      return;
    }

    if (
      !confirm(
        'Se adjuntarán los PDFs a las nóminas de Bisual y se crearán/actualizarán los registros en Holded (todas las filas pendientes). ¿Continuar?'
      )
    ) {
      return;
    }

    this.confirming = true;
    this.confirmDrafts(pendingIds, {
      closeOnSuccess: true,
      onDone: () => {
        this.confirming = false;
      },
      onError: () => {
        this.confirming = false;
      },
    });
  }

  private confirmDrafts(draftIds: number[], options: { closeOnSuccess?: boolean; onDone?: () => void; onError?: () => void } = {}): void {
    this.api.confirmPayrollGestoriaImport(this.data.import.id, draftIds).subscribe({
      next: result => {
        for (const draft of result.drafts ?? []) {
          this.replaceDraft(draft);
        }
        options.onDone?.();
        this.snack.open(`Holded: ${result.synced} ok, ${result.failed} errores`, 'Ok', { duration: 5000 });
        if (options.closeOnSuccess) {
          this.dialog_ref.close({ confirmed: true, result });
        }
      },
      error: err => {
        options.onError?.();
        const message = err?.error?.message || 'Error al sincronizar con Holded';
        this.snack.open(message, 'Ok', { duration: 5000 });
      },
    });
  }

  close(): void {
    this.dialog_ref.close({ confirmed: this.drafts.some(d => d.status === 'synced') });
  }
}
